import styled, { keyframes } from 'styled-components';

// Shimmer do skeleton de carregamento (mesmo padrao das demais listas).
const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

// ============================================================
// Container
// ============================================================
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100vw - 64px);
  min-height: 100vh;
  margin: 0 0 0 64px;
  padding: 0;
  box-sizing: border-box;
  background: var(--cco-bg);
  color: var(--cco-ink);

  @media (max-width: 720px) {
    width: 100vw;
    margin: 0;
  }
`;

// Wrapper passa-through pra back-compat
export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

// ============================================================
// Breadcrumb (.crumbs) — back-btn 34x34 + trail Dashboard › Notificações
// ============================================================
export const BreadcrumbWrapper = styled.section`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px var(--cco-pad);
  border-bottom: 1px solid var(--cco-line);

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

// ============================================================
// Toolbar (.toolbar) — filter-chips + sep + mark-all + search + exhibit
// ============================================================
export const Toolbar = styled.section`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 14px var(--cco-pad);
  border-bottom: 1px solid var(--cco-line);
`;

export const FilterGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

// FilterChip (.pill-btn) — pill rounded com counter "ct" ao lado.
// Estado "on" + variantes de cor por prop $variant ('red'|'green'|
// 'orange'|'blue'). aria-pressed='true' ativa.
export const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 999px;
  background: transparent;
  border: 1px solid var(--cco-line);
  color: var(--cco-ink-mute);
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;

  :hover {
    color: var(--cco-ink);
    background: rgba(255, 255, 255, 0.04);
  }

  /* Estado ativo via aria-pressed=true. Cor accent via prop $variant:
     blue (default) | red | green | orange. Expoe --chip-accent pra
     o FilterChipCount usar a cor solida sem cair na armadilha do
     currentColor (que resolveria pro var(--cco-bg) novo do badge). */
  &[aria-pressed='true'] {
    --chip-accent: ${(props) =>
      props.$variant === 'red'
        ? 'var(--cco-red)'
        : props.$variant === 'green'
        ? 'var(--cco-green)'
        : props.$variant === 'orange'
        ? 'var(--cco-orange)'
        : 'var(--cco-blue)'};
    color: var(--chip-accent);
    border-color: var(--chip-accent);
    background: ${(props) =>
      props.$variant === 'red'
        ? 'var(--cco-red-soft, rgba(244, 68, 92, 0.14))'
        : props.$variant === 'green'
        ? 'var(--cco-green-soft, rgba(43, 212, 125, 0.14))'
        : props.$variant === 'orange'
        ? 'var(--cco-orange-soft, rgba(241, 162, 91, 0.14))'
        : 'var(--cco-blue-soft, rgba(78, 161, 243, 0.14))'};
  }
`;

// Counter dentro do FilterChip (.ct). Cor sólida solo no chip ativo.
export const FilterChipCount = styled.span`
  min-width: 20px;
  height: 20px;
  padding: 0 7px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: var(--cco-ink);
  display: inline-grid;
  place-items: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;

  ${FilterChip}[aria-pressed='true'] & {
    background: var(--chip-accent);
    color: ${(props) =>
      props.$darkText ? '#0a0a0b' : 'var(--cco-bg)'};
  }
`;

// Separator vertical 1px (sep-v)
export const SepV = styled.span.attrs({ 'aria-hidden': true })`
  width: 1px;
  height: 28px;
  background: var(--cco-line);
`;

// Mark-all (botao "Marcar todas") — cyan-soft pill com check inline
export const MarkAll = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  background: var(--cco-cyan-soft, rgba(28, 176, 216, 0.14));
  color: var(--cco-cyan);
  border: 0;
  box-shadow: inset 0 0 0 1px rgba(28, 176, 216, 0.4);
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 150ms ease;
  font-family: inherit;

  :hover:not(:disabled) {
    background: rgba(28, 176, 216, 0.22);
  }

  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// Search inline — pill com lupa svg + native input transparente
export const SearchInline = styled.div`
  flex: 1;
  min-width: 240px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid var(--cco-line);
  border-radius: 999px;

  /* No phone (<=480) o min-width forcava overflow horizontal. */
  @media (max-width: 480px) {
    min-width: 0;
    width: 100%;
  }

  svg {
    width: 15px;
    height: 15px;
    color: var(--cco-ink-mute);
    flex: none;
  }

  input {
    flex: 1;
    background: transparent;
    border: 0;
    outline: 0;
    color: var(--cco-ink);
    font: inherit;
    font-size: 13.5px;
    min-width: 0;

    ::placeholder {
      color: var(--cco-ink-faint);
    }
  }
`;

// Exhibit — "Exibir N alertas" inline select
export const Exhibit = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--cco-ink-mute);
  font-size: 12.5px;
`;

export const PageSizePicker = styled.div`
  position: relative;
  z-index: 20;
`;

export const PageSizeButton = styled.button`
  min-width: 64px;
  height: 30px;
  padding: 0 28px 0 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--cco-line);
  color: var(--cco-ink);
  font-size: 13px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  position: relative;

  ::after {
    content: '';
    position: absolute;
    right: 9px;
    top: 50%;
    transform: translateY(-40%);
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid rgba(255, 255, 255, 0.45);
  }
`;

export const PageSizeMenu = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  min-width: 88px;
  padding: 6px;
  border-radius: 8px;
  background: #15161a;
  border: 1px solid var(--cco-line);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const PageSizeOption = styled.button`
  height: 30px;
  border: 0;
  border-radius: 6px;
  background: ${(props) =>
    props['aria-selected'] === 'true'
      ? 'var(--cco-blue-soft, rgba(78, 161, 243, 0.14))'
      : 'transparent'};
  color: ${(props) =>
    props['aria-selected'] === 'true' ? 'var(--cco-blue)' : 'var(--cco-ink)'};
  text-align: left;
  padding: 0 10px;
  font: inherit;
  font-size: 13px;
  cursor: pointer;

  :hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

// ============================================================
// Table (.table)
// ============================================================
export const TableWrap = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const Table = styled.div`
  display: grid;
  grid-template-columns: 1.45fr 1.25fr 1.35fr 0.95fr 0.95fr 1fr 112px;
  align-items: stretch;

  @media (max-width: 1280px) {
    grid-template-columns: 1.3fr 1.1fr 1.2fr 0.9fr 0.9fr 0.9fr 96px;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1.2fr 1fr 1fr 110px 110px;
    > .th.t-tipo,
    > .tr > .tc.tipo,
    > .th.t-acoes,
    > .tr > .tc.acoes {
      display: none;
    }
  }

  /* Mobile: vira card layout. Header rows ocultos, cada Tr empilha
     com data-label inline em cada Tc. Aqui voltamos a mostrar
     tipo/acoes (escondidos no breakpoint anterior) porque ja nao
     ha disputa por largura horizontal. */
  @media (max-width: 720px) {
    display: flex;
    flex-direction: column;

    > .th.t-tipo,
    > .tr > .tc.tipo,
    > .th.t-acoes,
    > .tr > .tc.acoes {
      display: flex;
    }
  }
`;

export const Th = styled.div.attrs((props) => ({
  className: ['th', props.$tipoCol && 't-tipo', props.$acoesCol && 't-acoes']
    .filter(Boolean)
    .join(' '),
}))`
  padding: 14px var(--cco-pad);
  color: var(--cco-ink);
  font-size: 13px;
  font-weight: 700;
  border-bottom: 1px solid var(--cco-line);

  @media (max-width: 720px) {
    display: none;
  }
`;

// Tr — display:contents pra cada Tc ir direto pro grid pai.
// .viz adiciona opacity 0.72 nas Tc filhas (linha visualizada).
export const Tr = styled.div.attrs((props) => ({
  className: ['tr', props.$viz && 'viz'].filter(Boolean).join(' '),
}))`
  display: contents;

  /* Linha clicavel (abre o alarme): cursor + hover mais forte. Como o Tr
     e display:contents (sem box proprio), o cursor vai nas celulas. */
  ${(props) => props.$clickable && '> .tc { cursor: pointer; }'}

  :hover > .tc {
    background: ${(props) =>
      props.$clickable
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(255, 255, 255, 0.025)'};
  }

  &.viz > .tc {
    opacity: 0.72;
  }

  @media (max-width: 720px) {
    display: flex;
    flex-direction: column;
    padding: 14px var(--cco-pad);
    border-bottom: 1px solid var(--cco-line);
    gap: 4px;
  }
`;

export const Tc = styled.div.attrs((props) => ({
  className: ['tc', props.$variant && props.$variant].filter(Boolean).join(' '),
}))`
  padding: 18px var(--cco-pad);
  border-bottom: 1px solid var(--cco-line);
  display: flex;
  align-items: center;
  transition: background 120ms ease;
  min-width: 0;

  /* Cliente — bullet vermelho pulsante + nome bold */
  &.cliente {
    gap: 12px;
    font-weight: 700;
    color: var(--cco-ink);
    font-size: 13.5px;
    line-height: 1.35;
  }
  &.cliente .bullet {
    width: 9px;
    height: 9px;
    border-radius: 999px;
    background: var(--cco-red);
    box-shadow: 0 0 0 3px rgba(244, 68, 92, 0.18);
    flex: none;
  }
  ${Tr}.viz &.cliente .bullet {
    background: var(--cco-green);
    box-shadow: 0 0 0 3px rgba(43, 212, 125, 0.18);
  }

  &.maquina {
    color: var(--cco-ink);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.02em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.variavel {
    color: var(--cco-ink);
    font-size: 13px;
    font-weight: 600;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.data {
    color: var(--cco-ink);
    font-size: 13px;
    font-variant-numeric: tabular-nums;
  }

  &.tipo {
    color: var(--cco-ink);
    font-size: 13.5px;
    font-weight: 500;
  }

  @media (max-width: 720px) {
    padding: 4px 0;
    border-bottom: 0;
    justify-content: flex-start;
    /* Label inline (data-label do JSX). */
    &::before {
      content: attr(data-label);
      color: var(--cco-ink-mute);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      margin-right: 8px;
      min-width: 70px;
      flex-shrink: 0;
    }
  }
`;

// Celula skeleton (loading) — barra shimmer no lugar do texto. Mesmo
// padrao das demais listas (users/machines/stations/customers).
export const SkeletonCell = styled(Tc)`
  ::before {
    content: '';
    display: block;
    width: ${(props) => props.$w || '70%'};
    height: 14px;
    border-radius: 6px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.04) 0%,
      rgba(255, 255, 255, 0.08) 50%,
      rgba(255, 255, 255, 0.04) 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.4s ease-in-out infinite;
  }
`;

// Status pill — pendente (red-soft) / visualizado (green solid) /
// atencao (orange-soft).
export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.06em;

  ::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 999px;
  }

  ${(props) =>
    props.$variant === 'pend' &&
    `
    background: var(--cco-red-soft, rgba(244,68,92,0.14));
    color: var(--cco-red);
    box-shadow: inset 0 0 0 1px rgba(244, 68, 92, 0.35);
    ::before { background: var(--cco-red); }
  `}

  ${(props) =>
    props.$variant === 'viz' &&
    `
    background: var(--cco-green);
    color: #06281a;
    ::before { background: #06281a; }
  `}

  ${(props) =>
    props.$variant === 'aten' &&
    `
    background: var(--cco-orange-soft, rgba(241,162,91,0.14));
    color: var(--cco-orange);
    box-shadow: inset 0 0 0 1px rgba(241, 162, 91, 0.4);
    ::before { background: var(--cco-orange); }
  `}
`;

// Acoes — botoes 32x32 (check verde / view azul)
export const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ActBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: transparent;
  border: 0;
  display: grid;
  place-items: center;
  color: var(--cco-ink-mute);
  transition: all 150ms ease;
  cursor: pointer;
  font-family: inherit;

  &.check:hover:not(:disabled) {
    color: var(--cco-green);
    background: var(--cco-green-soft, rgba(43, 212, 125, 0.14));
  }
  &.check.done {
    color: var(--cco-green);
  }
  &.view {
    color: var(--cco-blue);
  }
  &.view:hover:not(:disabled) {
    background: var(--cco-blue-soft, rgba(78, 161, 243, 0.14));
  }

  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const EmptyRow = styled.div`
  grid-column: 1 / -1;
  padding: 80px 0;
  text-align: center;
  color: var(--cco-ink-faint);
  font-style: italic;
`;

// ============================================================
// Pager (.pager) — Anterior / 1 2 3 ... 45 46 47 / Proximo
// ============================================================
export const Pager = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 20px var(--cco-pad);
  border-top: 1px solid var(--cco-line);
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 8px;
    padding: 14px var(--cco-pad);
    .btn {
      padding: 0 12px;
      font-size: 11.5px;
    }
    .pg {
      width: 32px;
      height: 32px;
      font-size: 12.5px;
    }
  }

  .btn {
    height: 36px;
    padding: 0 18px;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid var(--cco-line);
    color: var(--cco-ink);
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    transition: all 150ms ease;
    cursor: pointer;
    font-family: inherit;
  }
  .btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
  }
  .btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .pages {
    display: flex;
    gap: 6px;
    align-items: center;
  }
  .pg {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    display: grid;
    place-items: center;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid var(--cco-line);
    color: var(--cco-ink);
    font-size: 13.5px;
    font-weight: 600;
    transition: all 150ms ease;
    cursor: pointer;
    font-family: inherit;
  }
  .pg.on {
    background: var(--cco-cyan);
    border-color: var(--cco-cyan);
    color: #0a0a0b;
  }
  .pg:not(.on):hover {
    background: rgba(255, 255, 255, 0.08);
  }
  .dots {
    color: var(--cco-ink-mute);
    padding: 0 4px;
    font-weight: 700;
  }
`;

// ============================================================
// Compat exports (pra back-compat com imports antigos)
// ============================================================
export const TableWrapper = TableWrap;
export const PaginationContainer = Pager;
export const PaginationButton = styled.button``;
export const PaginationPage = styled.span``;
export const QuantidadeWrapper = Exhibit;
export const Label = styled.label`
  font-size: 12.5px;
  color: var(--cco-ink-mute);
`;
export const BulkActionButton = MarkAll;
// dropdownStyles antigo (react-select) — vazio pra nao quebrar imports
export const dropdownStyles = () => ({});
